import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ArrowLeft, ChevronLeft, ChevronRight, Gem, HelpCircle, Loader2, Send, Wallet } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTeamStore } from '../store/useTeamStore'
import AnimatedNumber from '../../../components/AnimatedNumber'
import HelpModal from '../../../components/HelpModal'
import ProfileLink from '../../../components/ProfileLink'
import { canPayTreasury, personName, teamErrorText, treasuryTypeLabel } from '../utils/teamFormat'
import { accentColor, lightenHex } from '../utils/teamColors'
import { formatAgo } from '../../profile/utils/profileFormat'
import { CARD_CLASS, SECTION_VARIANT, SECTIONS_VARIANT } from '../utils/teamUi'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import { sendYandexGoal } from '../../../services/yandexMetrikaService'

const PAGE_SIZE = 20

/**
 * Казна банды (`/teams/treasury`): журнал операций доступен любому участнику,
 * выплата — создателю и заместителю.
 *
 * Казна пополняется наградами за топы дня, ивенты и захваты режимов. Выплата
 * приходит получателю на баланс сразу, без комиссии и отыгрыша, поэтому после
 * успеха стор перезапрашивает `/api/me/`.
 */
const TeamTreasury = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    team, isTeamLoading, treasury, isTreasuryLoading, isMutating,
    loadTeam, loadTreasury, payFromTreasury
  } = useTeamStore()

  const [page, setPage] = useState(1)
  const [targetChatId, setTargetChatId] = useState('')
  const [amount, setAmount] = useState('')
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  useEffect(() => {
    loadTeam('my')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadTreasury(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  if (isTeamLoading && !team) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={24} className="animate-spin text-accent-primary" />
      </div>
    )
  }

  // Журнал доступен только участникам — без банды экран пустой
  if (!team) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center px-4 py-16 text-center">
        <span className="mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-surface">
          <Wallet size={36} className="text-text-secondary" />
        </span>
        <p className="mb-6 text-sm text-text-secondary">
          {t('team.err.notInTeam', 'Ты не состоишь в банде')}
        </p>
        <button
          onClick={() => navigate('/teams')}
          className="rounded-xl border border-accent-primary/30 bg-accent-primary/10 px-6 py-3 font-medium text-accent-primary transition-all active:scale-[0.98]"
        >
          {t('team.toCatalog', 'В каталог банд')}
        </button>
      </div>
    )
  }

  const accent = accentColor(team.colors)
  const canPay = canPayTreasury(team.my_role) && team.status === 'active'
  const payableMembers = Array.isArray(team.members)
    ? team.members.filter((member) => member.chat_id != null)
    : []

  const numericAmount = Number(amount)
  const isAmountValid = Number.isInteger(numericAmount) && numericAmount >= 1 &&
    numericAmount <= (team.treasury ?? 0)

  const handlePay = async () => {
    if (!targetChatId || !isAmountValid || isMutating) return
    hapticImpact('heavy')

    const res = await payFromTreasury(Number(targetChatId), numericAmount)
    if (res?.ok) {
      hapticNotify('success')
      sendYandexGoal('team-treasury-pay')
      toast.success(t('team.paySuccess', 'Выплата отправлена'))
      setAmount('')
      setTargetChatId('')
      setPage(1)
    } else if (res) {
      toast.error(teamErrorText(res.error, t, res))
    }
  }

  const pages = Math.max(1, Math.ceil((treasury.total || 0) / PAGE_SIZE))

  return (
    <div className="mx-auto w-full px-4 py-6 lg:max-w-3xl lg:px-6 lg:py-8">
      <motion.div variants={SECTIONS_VARIANT} initial="hidden" animate="visible" className="space-y-5">
        <motion.div variants={SECTION_VARIANT} className="flex items-center gap-3">
          <button
            onClick={() => navigate('/teams/my')}
            className="rounded-lg bg-surface p-2 text-white transition-all active:scale-[0.98]"
            aria-label={t('common.back', 'Назад')}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl text-white">{t('team.treasury', 'Казна банды')}</h1>
          <button
            type="button"
            onClick={() => {
              hapticImpact('light')
              setIsHelpOpen(true)
            }}
            className="rounded-full p-1.5 transition-colors active:scale-[0.98]"
            style={{ backgroundColor: `${accent}1A`, color: accent }}
            aria-label={t('team.treasuryHelp.button', 'Как работает казна?')}
          >
            <HelpCircle size={18} />
          </button>
        </motion.div>

        {/* Остаток казны */}
        <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} relative overflow-hidden p-5`}>
          <div
            className="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full blur-2xl"
            style={{ backgroundColor: `${accent}1A` }}
          />
          <div className="relative">
            <div className="mb-1 flex items-center gap-2">
              <Gem size={16} style={{ color: accent }} />
              <span className="text-xs uppercase tracking-wide text-text-secondary">
                {t('team.treasuryBalance', 'Остаток казны')}
              </span>
            </div>
            <div
              className="bg-clip-text text-3xl font-bold tabular-nums text-transparent"
              style={{ backgroundImage: `linear-gradient(90deg, ${accent}, ${lightenHex(accent)})` }}
            >
              <AnimatedNumber value={team.treasury ?? 0} />
            </div>
          </div>
        </motion.div>

        {/* Выплата участнику — создатель и заместитель */}
        {canPay && payableMembers.length > 0 && (
          <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-4`}>
            <h2 className="mb-3 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
              {t('team.payTitle', 'Выплата участнику')}
            </h2>

            <select
              value={targetChatId}
              onChange={(e) => setTargetChatId(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-dark-primary px-4 py-3 text-sm text-white focus:border-accent-primary focus:outline-none"
            >
              <option value="">{t('team.paySelectMember', 'Выбери получателя')}</option>
              {payableMembers.map((member) => (
                <option key={member.chat_id} value={member.chat_id}>
                  {personName(member, t)}
                </option>
              ))}
            </select>

            <div className="mt-2 flex gap-2">
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
                inputMode="numeric"
                placeholder={t('team.payAmount', 'Сумма')}
                className="min-w-0 flex-1 rounded-xl border border-white/10 bg-dark-primary px-4 py-3 text-sm tabular-nums text-white placeholder:text-text-secondary/60 focus:border-accent-primary focus:outline-none"
              />
              <button
                onClick={handlePay}
                disabled={!targetChatId || !isAmountValid || isMutating}
                className="flex shrink-0 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-5 py-3 text-sm font-bold text-dark-primary transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {isMutating ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {t('team.paySubmit', 'Выплатить')}
              </button>
            </div>

            {amount && !isAmountValid && (
              <p className="mt-2 text-xs text-red-400">
                {numericAmount > (team.treasury ?? 0)
                  ? t('team.err.notEnoughTreasury', 'В казне недостаточно кристаллов')
                  : t('team.err.invalidAmount', 'Неверная сумма')}
              </p>
            )}
          </motion.div>
        )}

        {/* Журнал операций */}
        <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-4`}>
          <h2 className="mb-3 text-sm font-bold not-italic uppercase tracking-wide text-text-secondary">
            {t('team.treasuryHistory', 'Журнал казны')}
          </h2>

          {isTreasuryLoading && treasury.operations.length === 0 ? (
            <div className="flex justify-center py-6">
              <Loader2 size={20} className="animate-spin text-accent-primary" />
            </div>
          ) : treasury.operations.length === 0 ? (
            <p className="text-sm text-text-secondary">
              {t('team.treasuryEmpty', 'Операций пока не было')}
            </p>
          ) : (
            <div className="space-y-2">
              {treasury.operations.map((op) => (
                <div key={op.id} className="rounded-xl border border-white/5 bg-dark-primary p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white">{treasuryTypeLabel(op.type, t, op.award, op.capture, treasury.receivedAt)}</p>
                      <p className="mt-0.5 text-[11px] text-text-secondary">
                        {!['top_award', 'capture_award', 'halloween_award'].includes(op.type) && (
                          <>
                            {/* actor: null означает администрацию — имя сотрудника наружу не отдаётся */}
                            <ProfileLink slug={op.actor?.slug}>
                              {personName(op.actor, t)}
                            </ProfileLink>
                            {op.target && (
                              <>
                                {' → '}
                                <ProfileLink slug={op.target.slug}>
                                  {personName(op.target, t)}
                                </ProfileLink>
                              </>
                            )}
                            {' · '}
                          </>
                        )}
                        {formatAgo(op.ago, t)}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      {/* Знак уже в amount: плюс только у admin_add */}
                      <p className={`text-sm font-bold tabular-nums ${Number(op.amount) > 0 ? 'text-accent-primary' : 'text-red-400'}`}>
                        {Number(op.amount) > 0 ? '+' : ''}{op.amount}
                      </p>
                      <p className="text-[11px] tabular-nums text-text-secondary">
                        {t('team.treasuryAfter', 'остаток {{balance}}', { balance: op.balance })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isTreasuryLoading}
                className="rounded-xl border border-white/10 bg-surface p-2.5 text-white transition-all active:scale-[0.98] disabled:opacity-40"
                aria-label={t('common.back', 'Назад')}
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm tabular-nums text-text-secondary">{page} / {pages}</span>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages || isTreasuryLoading}
                className="rounded-xl border border-white/10 bg-surface p-2.5 text-white transition-all active:scale-[0.98] disabled:opacity-40"
                aria-label={t('team.nextPage', 'Дальше')}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={t('team.treasuryHelp.title', 'Как работает казна?')}
        steps={[
          {
            title: t('team.treasuryHelp.incomeTitle', 'Пополнение казны'),
            description: t('team.treasuryHelp.incomeDescription', 'Кристаллы попадают в казну за места в топах дня во всех режимах, участие и победы в ивентах, а также захват режимов.')
          },
          {
            title: t('team.treasuryHelp.paymentsTitle', 'Выплаты участникам'),
            description: t('team.treasuryHelp.paymentsDescription', 'Создатель и заместители могут отправлять кристаллы участникам банды. Средства поступают на баланс сразу, без комиссии и отыгрыша.')
          }
        ]}
        highlightLastStep={false}
      />
    </div>
  )
}

export default TeamTreasury
